import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Transition from '../../utils/Transition';
import imageSrc from '../../utils/imageData';
import LoadingSpinner from '../../components/LoadingSpinner';

// Add this at the top of your file, outside the component
const COUNTRIES_DATA = [
  { code: 'AR', name: 'Argentina' },
  { code: 'BO', name: 'Bolivia' },
  { code: 'BR', name: 'Brazil' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CU', name: 'Cuba' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' },
  { code: 'SV', name: 'El Salvador' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'HN', name: 'Honduras' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'PA', name: 'Panama' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'PE', name: 'Peru' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'ES', name: 'Spain' }
];

const SECTORS_DATA = [
  { id: 1, name: 'Finance/Banking' },
  { id: 3, name: 'Insurance' },
  { id: 4, name: 'Healthcare' },
  { id: 5, name: 'Pharmaceuticals' },
  { id: 6, name: 'Energy' },
  { id: 7, name: 'Utilities' },
  { id: 8, name: 'Telecommunications' },
  { id: 9, name: 'Technology' },
  { id: 10, name: 'Cybersecurity' },
  { id: 11, name: 'Manufacturing' },
  { id: 12, name: 'Retail' },
  { id: 13, name: 'Transportation' },
  { id: 14, name: 'Government' },
  { id: 15, name: 'Education' }
];

function OrgsSidebar({
  organizationsSidebarOpen,
  setOrganizationsSidebarOpen,
  organizationType,
  selectedOrganization = null,
  onSaved
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    country: '',
    city: '',
    logo: '',
    sector: '',
    type: organizationType,
    regulatoryBodies: [],
    contactInfo: {
      address: '',
      phone: '',
      email: '',
      website: ''
    }
  });
  
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [regulatoryBodies, setRegulatoryBodies] = useState([]);
  
  // Load countries, sectors, and regulatory bodies on mount
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        setCountries(COUNTRIES_DATA);
        setSectors(SECTORS_DATA);
        
        const regulatoryResponse = await axios.get(
          `${Config.APIBaseURL}/customers/regulator`, 
          getAxiosConfig()
        );
        setRegulatoryBodies(regulatoryResponse.data);
      } catch (error) {
        console.error('Error fetching regulator data:', error);
      }
    };
    
    fetchReferenceData();
  }, []);
  
  // Reset form when sidebar opens or organization type changes
  useEffect(() => {
    if (organizationsSidebarOpen) {
      if (selectedOrganization) {
        setFormData({
          name: selectedOrganization.name || '',
          short_name: selectedOrganization.short_name || '',
          country: selectedOrganization.country || '',
          city: selectedOrganization.city || '',
          logo: selectedOrganization.logo
            ? imageSrc(selectedOrganization.logo)
            : '',
          sector: (SECTORS_DATA.find(s => s.name === selectedOrganization.sector)?.id || selectedOrganization.sector || '').toString(),
          type: organizationType,
          regulatoryBodies: selectedOrganization.regulatoryBodies || [],
          contactInfo: {
            address: selectedOrganization.address || '',
            phone: selectedOrganization.phone || '',
            email: selectedOrganization.email || '',
            website: selectedOrganization.website || ''
          }
        });
      } else {
        // Create mode - reset form
        setFormData({
          name: '',
          short_name: '',
          country: '',
          city: '',
          logo: '',
          sector: organizationType === 'regulator' ? '14' : '',
          type: organizationType,
          regulatoryBodies: [],
          contactInfo: {
            address: '',
            phone: '',
            email: '',
            website: ''
          }
        });
      }
      setErrors({});
    }
  }, [organizationsSidebarOpen, selectedOrganization, organizationType]);
  
  const getAxiosConfig = () => ({
    headers: {
      'access_token': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        logo: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      
      // Find the sector name based on the selected ID
      const sectorName = sectors.find(sector => 
        sector.id === Number(formData.sector) || 
        sector.id.toString() === formData.sector
      )?.name || "";
      
      // Make sure type is set correctly
      const type = formData.type || organizationType;
      
      const payload = {
        name: formData.name,
        short_name: formData.short_name,
        country: formData.country,
        city: formData.city,
        sector: sectorName || formData.sector,
        type: type,
        address: formData.contactInfo.address,
        phone: formData.contactInfo.phone,
        email: formData.contactInfo.email,
        website: formData.contactInfo.website
      };

      if (formData.logo && formData.logo.startsWith('data:image')) {
        payload.logo = formData.logo;
      } else if (!selectedOrganization) {
        payload.logo = formData.logo || 'images/logos/default.png';
      }
      
      // Only include regulatoryBodies if needed
      if (type === 'business' && formData.regulatoryBodies?.length > 0) {
        payload.regulatoryBodies = formData.regulatoryBodies;
      }
      
      const axiosConfig = getAxiosConfig();
      
      let response;
      if (selectedOrganization) {
        // Update existing organization
        response = await axios.put(
          `${Config.APIBaseURL}/customer/update/${selectedOrganization.id}`,
          payload,
          axiosConfig
        );
      } else {
        // Create new organization
        response = await axios.post(
          `${Config.APIBaseURL}/customer/create`,
          payload,
          axiosConfig
        );
      }
      
      if (response.status === 200 || response.status === 201) {
        setOrganizationsSidebarOpen(false);
        if (onSaved) onSaved();
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      if (error.response?.data?.detail) {
        setErrors({ general: error.response.data.detail });
      } else if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Error saving organization. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Transition
      show={organizationsSidebarOpen}
      tag="div"
      className="fixed inset-0 overflow-hidden z-50"
      enter="transition ease-in-out duration-200"
      enterStart="opacity-0"
      enterEnd="opacity-100"
      leave="transition ease-in-out duration-200"
      leaveStart="opacity-100"
      leaveEnd="opacity-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <Transition
          show={organizationsSidebarOpen}
          tag="div"
          className="absolute inset-0 bg-slate-900 bg-opacity-30"
          enter="transition ease-out duration-200"
          enterStart="opacity-0"
          enterEnd="opacity-100"
          leave="transition ease-out duration-200"
          leaveStart="opacity-100"
          leaveEnd="opacity-0"
          onClick={() => setOrganizationsSidebarOpen(false)}
        />
        
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
          <Transition
            show={organizationsSidebarOpen}
            tag="div"
            className="w-screen max-w-md"
            enter="transition ease-in-out duration-500 transform"
            enterStart="translate-x-full"
            enterEnd="translate-x-0"
            leave="transition ease-in-out duration-500 transform"
            leaveStart="translate-x-0"
            leaveEnd="translate-x-full"
          >
            <div className="h-full flex flex-col bg-white dark:bg-slate-800 shadow-xl overflow-y-scroll">
              <div className="px-4 sm:px-6 py-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    {selectedOrganization ? 'Edit' : 'Add'} {organizationType === 'business' ? 'Business' : 'Regulator'}
                  </h2>
                  <button
                    className="text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400"
                    onClick={() => setOrganizationsSidebarOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
                      <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                    </svg>
                  </button>
                </div>
                
                {errors.general && (
                  <div className="mb-4 px-4 py-2 bg-red-100 dark:bg-red-400/10 text-red-600 dark:text-red-400 rounded">
                    {errors.general}
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    {/* Logo upload */}
                    <div>
                      <div className="flex items-center">
                        {formData.logo ? (
                          <div className="mr-4">
                            <img src={formData.logo} alt="Logo preview" className="w-16 h-16 rounded" />
                          </div>
                        ) : null}
                        <div>
                          <label className="block text-sm font-medium mb-1" htmlFor="logo">
                            Organization Logo
                          </label>
                          <input
                            id="logo"
                            type="file"
                            className="form-input w-full px-2 py-1"
                            accept="image/*"
                            onChange={handleLogoChange}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="name">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        name="name"
                        className={`form-input w-full ${errors.name ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {errors.name && (
                        <div className="text-xs mt-1 text-red-500">{errors.name}</div>
                      )}
                    </div>
                    
                    {/* Short Name */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="short_name">
                        Short Name / Acronym
                      </label>
                      <input
                        id="short_name"
                        name="short_name"
                        className="form-input w-full px-2 py-1 border-slate-200 dark:border-slate-700"
                        type="text"
                        placeholder="E.g., IBM, WHO"
                        value={formData.short_name}
                        onChange={handleChange}
                      />
                      <div className="text-xs mt-1 text-slate-500">An abbreviation or acronym for the organization</div>
                    </div>
                    
                    {/* Country */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="country">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="country"
                        name="country"
                        className={`form-select w-full ${errors.country ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        value={formData.country}
                        onChange={handleChange}
                      >
                        <option value="">Select a country</option>
                        {countries.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      {errors.country && (
                        <div className="text-xs mt-1 text-red-500">{errors.country}</div>
                      )}
                    </div>
                    
                    {/* City */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="city">
                        City
                      </label>
                      <input
                        id="city"
                        name="city"
                        className="form-input w-full px-2 py-1 border-slate-200 dark:border-slate-700"
                        type="text"
                        value={formData.city}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* Sector (formerly Industry) */}
                    <div>
                      <label className="block text-sm font-medium mb-1" htmlFor="sector">
                        Sector <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="sector"
                        name="sector"
                        className={`form-select w-full ${errors.sector ? 'border-red-300' : 'border-slate-200 dark:border-slate-700'}`}
                        value={formData.sector}
                        onChange={handleChange}
                        disabled={organizationType === 'regulator'}
                      >
                        <option value="">Select a sector</option>
                        {sectors.map(sector => (
                          <option key={sector.id} value={sector.id}>
                            {sector.name}
                          </option>
                        ))}
                      </select>
                      {errors.sector && (
                        <div className="text-xs mt-1 text-red-500">{errors.sector}</div>
                      )}
                      {organizationType === 'regulator' && (
                        <div className="text-xs mt-1 text-slate-500">Regulators are automatically assigned to the Government sector</div>
                      )}
                    </div>
                    
                    {/* Regulatory Bodies */}
                    {organizationType === 'business' && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Applicable Regulatory Bodies
                        </label>
                        <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded p-2">
                          {regulatoryBodies.map(body => (
                            <label key={body.id} className="flex items-center">
                              <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={formData.regulatoryBodies.includes(body.id)}
                                onChange={() => {
                                  const newBodies = formData.regulatoryBodies.includes(body.id)
                                    ? formData.regulatoryBodies.filter(id => id !== body.id)
                                    : [...formData.regulatoryBodies, body.id];
                                  
                                  setFormData(prev => ({
                                    ...prev,
                                    regulatoryBodies: newBodies
                                  }));
                                }}
                              />
                              <span className="ml-2 text-sm">{body.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Contact Information */}
                    <div>
                      <h3 className="text-lg font-medium mb-2 text-slate-800 dark:text-slate-200">
                        Contact Information
                      </h3>
                      
                      {/* Address */}
                      <div className="mb-2">
                        <label className="block text-sm font-medium mb-1" htmlFor="contactInfo.address">
                          Address
                        </label>
                        <textarea
                          id="contactInfo.address"
                          name="contactInfo.address"
                          className="form-textarea w-full px-2 py-1 border-slate-200 dark:border-slate-700"
                          rows="2"
                          value={formData.contactInfo.address}
                          onChange={handleChange}
                        />
                      </div>
                      
                      {/* Phone */}
                      <div className="mb-2">
                        <label className="block text-sm font-medium mb-1" htmlFor="contactInfo.phone">
                          Phone
                        </label>
                        <input
                          id="contactInfo.phone"
                          name="contactInfo.phone"
                          className="form-input w-full px-2 py-1 border-slate-200 dark:border-slate-700"
                          type="text"
                          value={formData.contactInfo.phone}
                          onChange={handleChange}
                        />
                      </div>
                      
                      {/* Email */}
                      <div className="mb-2">
                        <label className="block text-sm font-medium mb-1" htmlFor="contactInfo.email">
                          Email
                        </label>
                        <input
                          id="contactInfo.email"
                          name="contactInfo.email"
                          className="form-input w-full px-2 py-1 border-slate-200 dark:border-slate-700"
                          type="email"
                          value={formData.contactInfo.email}
                          onChange={handleChange}
                        />
                      </div>
                      
                      {/* Website */}
                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor="contactInfo.website">
                          Website
                        </label>
                        <input
                          id="contactInfo.website"
                          name="contactInfo.website"
                          className="form-input w-full px-2 py-1 border-slate-200 dark:border-slate-700"
                          type="url"
                          placeholder="https://example.com"
                          value={formData.contactInfo.website || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Form buttons */}
                  <div className="flex items-center justify-end mt-6">
                    <button
                      type="button"
                      className="btn-sm border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-300"
                      onClick={() => setOrganizationsSidebarOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white ml-3"
                      disabled={isLoading}
                    >
                      {isLoading ? <LoadingSpinner small /> : selectedOrganization ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  );
}

export default OrgsSidebar;